import { DateValue } from '../components/Form/DateInput';

export const parseDate = (date: Date | string): Date =>
  typeof date === 'string' ? new Date(date) : date;

export const parseOptionalDate = (rawDate?: string | Date | null): Date | null => {
  const date = rawDate ? parseDate(rawDate) : null;

  if (date === null || Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
};

const isNaNValueOrEmptyString = (number?: string): boolean => {
  if (number === '') {
    return true;
  }
  return Number.isNaN(Number(number));
};

export const getStartOfDay = (initialDateTime: Date): Date => {
  const finalDateTime = new Date(initialDateTime);
  finalDateTime.setUTCHours(0, 0, 0, 0);
  return finalDateTime;
};

export const dateToDateValue = (date: Date): DateValue => {
  return {
    day: date.getDate().toString(),
    month: (date.getMonth() + 1).toString(),
    year: date.getFullYear().toString(),
  };
};

export const dateValueToUtcDate = (dateValue: DateValue): Date | null => {
  if (
    isNaNValueOrEmptyString(dateValue.day) ||
    isNaNValueOrEmptyString(dateValue.month) ||
    isNaNValueOrEmptyString(dateValue.year)
  ) {
    return null;
  }

  const convertedDate = new Date(
    Date.UTC(Number(dateValue.year), Number(dateValue.month) - 1, Number(dateValue.day))
  );

  // This is needed as XX is treated as 19XX in new Date()
  convertedDate.setUTCFullYear(Number(dateValue.year));

  return convertedDate;
};

export const dateValueToLocalDate = (dateValue: DateValue): Date | null => {
  if (
    isNaNValueOrEmptyString(dateValue.day) ||
    isNaNValueOrEmptyString(dateValue.month) ||
    isNaNValueOrEmptyString(dateValue.year)
  ) {
    return null;
  }

  const convertedDate = new Date(
    Number(dateValue.year),
    Number(dateValue.month) - 1,
    Number(dateValue.day)
  );

  // This is needed as XX is treated as 19XX in new Date()
  convertedDate.setFullYear(Number(dateValue.year));

  return convertedDate;
};

/**
 * Coverts a date value to an ISO string in the format 2021-03-31T12:34:56
 */
export const dateValueToISOString = (dateValue: DateValue): string | undefined => {
  const date = dateValueToUtcDate(dateValue);
  return date ? date.toISOString() : undefined;
};

/**
 * Coverts a date value to an ISO date string in the format 2021-03-31
 */
export const dateValueToISODateString = (dateValue: DateValue): string | undefined => {
  const dateTime = dateValueToISOString(dateValue);
  return dateTime?.split('T')[0];
};

/*
 * Extracts the calendar date in the local timezone and converts to the same date at midnight UTC
 *
 * e.g. 2020-01-01T00:30:00+01:00 becomes 2020-01-01T00:00:00Z
 *
 * This is important when mapping values from the calendar input:
 * it returns date objects at midnight local time. We want to preserve the calendar date but
 * convert to midnight UTC for the API (even if the date object would actually represents a different date in UTC).
 */
export const localDateToUtcDate = (date: Date): Date => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
};

/**
 * Extracts the calendar date in UTC and converts to the same date at midnight local time
 */
export const utcDateToLocalDate = (date: Date): Date => {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

/*
 * This works due to JavaScript overflowing dates. For example, new Date(2000, 1, 31) is the
 * 2nd March 2000 (noting that months start at 0). So if the returned Date has a day or
 * month value different from the input value, it must have overflowed and been an incorrect
 * date
 */
export const isDateValueValidDate = (dateValue: DateValue): boolean => {
  const date = dateValueToUtcDate(dateValue);
  return (
    date !== null &&
    date.getUTCDate() === Number(dateValue.day) &&
    date.getUTCMonth() + 1 === Number(dateValue.month)
  );
};

export const addYearsToDate = (date: Date, amount: number): Date =>
  new Date(new Date(date).setUTCFullYear(date.getUTCFullYear() + amount));

export const addMonthsToDate = (date: Date, amount: number): Date =>
  new Date(new Date(date).setUTCMonth(date.getUTCMonth() + amount));

export const addDaysToDate = (date: Date, amount: number): Date =>
  new Date(new Date(date).setUTCDate(date.getUTCDate() + amount));

export const numericDay = new Intl.DateTimeFormat('en', { day: 'numeric' });
export const numericMonth = new Intl.DateTimeFormat('en', { month: 'numeric' });
export const shortMonth = new Intl.DateTimeFormat('en', { month: 'short' });
export const longMonth = new Intl.DateTimeFormat('en', { month: 'long' });
export const numericYear = new Intl.DateTimeFormat('en', { year: 'numeric' });

/**
 * Formats to a date like `1/10/2017`
 */
export const formatNumericDate = (date: Date | string): string => {
  const parsedDate = parseDate(date);
  return `${numericDay.format(parsedDate)}/${numericMonth.format(
    parsedDate
  )}/${numericYear.format(parsedDate)}`;
};

/**
 * Formats to a date like `1 Oct 2017` or `1, Oct 2017`
 */
export const formatShortDate = (date: Date | string, comma = false): string => {
  const parsedDate = parseDate(date);
  return `${numericDay.format(parsedDate)}${comma ? ',' : ''} ${shortMonth.format(
    parsedDate
  )} ${numericYear.format(parsedDate)}`;
};

/**
 * Format a date like `October 1, 2017`
 */
export const formatLongDate = (date: Date | string): string => {
  const parsedDate = parseDate(date);
  return `${longMonth.format(parsedDate)} ${numericDay.format(
    parsedDate
  )}, ${numericYear.format(parsedDate)}`;
};

/**
 * Format a date like `October 2017`
 */
export const formatLongDateNoDay = (date: Date | string): string => {
  const parsedDate = parseDate(date);
  return `${longMonth.format(parsedDate)} ${numericYear.format(parsedDate)}`;
};

/**
 * Formats to a date like `1 October 2017`
 */
export const formatLongDateWithDayFirst = (date: Date | string): string => {
  const parsedDate = parseDate(date);
  return `${numericDay.format(parsedDate)} ${longMonth.format(
    parsedDate
  )} ${numericYear.format(parsedDate)}`;
};

export const addOrdinalSuffix = (day: number): string => {
  const remainder10 = day % 10;
  const remainder100 = day % 100;
  if (remainder10 === 1 && remainder100 !== 11) {
    return `${day}st`;
  }
  if (remainder10 === 2 && remainder100 !== 12) {
    return `${day}nd`;
  }
  if (remainder10 === 3 && remainder100 !== 13) {
    return `${day}rd`;
  }
  return `${day}th`;
};

export const addLeadingZerosToDateValue = (dateValue: DateValue): DateValue => {
  const { day, month, year } = dateValue;
  const parsedDay = day?.length === 1 ? `0${day}` : day;
  const parsedMonth = month?.length === 1 ? `0${month}` : month;
  return {
    day: parsedDay,
    month: parsedMonth,
    year,
  };
};

/**
 * Checks whether the first argument belongs to the same day as, or a later day than, the second argument.
 *
 * For example:
 *  - isSameOrLaterDateThan(today, tomorrow) = false
 *  - isSameOrLaterDateThan(today, today) = true
 *  - isSameOrLaterDateThan(today, yesterday) = true
 *  - isSameOrLaterDateThan(today at 6am, today at 6pm) = true
 */
export const isSameOrLaterDateThan = (dateTime1: Date, dateTime2: Date): boolean => {
  return dateTime1.getTime() >= getStartOfDay(dateTime2).getTime();
};

/**
 * Gets how far in the future the second dateTime is
 * It rounds down and ignores the date
 *
 * For example:
 * getDifferenceInMonths(
 *    new Date(2008, 10, 4), // November 4th, 2008
 *    new Date(2010, 2, 12)  // March 12th, 2010
 * ) = 16
 *
 * getDifferenceInMonths(
 *    new Date(2010, 0, 1),  // January 1st, 2010
 *    new Date(2010, 2, 12)  // March 12th, 2010
 * ) = 2
 *
 * getDifferenceInMonths(
 *    new Date(2010, 1, 1),  // February 1st, 2010
 *    new Date(2010, 2, 12)  // March 12th, 2010
 * ) = 1
 */

export const getDifferenceInMonths = (dateTime1: Date, dateTime2: Date): number => {
  const months =
    (dateTime2.getFullYear() - dateTime1.getFullYear()) * 12 -
    dateTime1.getMonth() +
    dateTime2.getMonth();
  return months <= 0 ? 0 : months;
};

/**
 * Gets how far in the future the second dateTime is in full days
 * It rounds down
 *
 * For example:
 * getDifferenceInDays(
 *    new Date(2008, 10, 4), // October 4th, 2008
 *    new Date(2008, 10, 4)  // October 4th, 2008
 * ) = 0
 *
 * getDifferenceInDays(
 *    new Date(2008, 10, 4), // October 4th, 2008
 *    new Date(2008, 10, 5)  // October 5th, 2008
 * ) = 1
 *
 * getDifferenceInDays(
 *    new Date(2008, 10, 4), // October 4th, 2008
 *    new Date(2008, 10, 4)  // October 31st, 2008
 * ) = 27
 */

export const getDifferenceInDays = (dateTime1: Date, dateTime2: Date): number => {
  const startOfDate1 = getStartOfDay(dateTime1);
  const startOfDate2 = getStartOfDay(dateTime2);

  const ONE_DAY = 24 * 60 * 60 * 1000; // hours * minutes * seconds * miliseconds

  return Math.floor((startOfDate2.getTime() - startOfDate1.getTime()) / ONE_DAY);
};

export const dateIsInThePast = (date: Date): boolean => {
  return localDateToUtcDate(date) < localDateToUtcDate(new Date());
};

export const getNoOfDaysInMonth = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

export type TimeToEvent = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

const SECONDS_IN_A_DAY = 86400; // 24 * 60 * 60
const SECONDS_IN_AN_HOUR = 3600; // 60 * 60
const SECONDS_IN_A_MINUTE = 60;
const MINUTES_IN_AN_HOUR = 60;
const HOURS_IN_A_DAY = 24;

export const NO_TIME_LEFT_TO_EVENT = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
};

export const getTimeToEvent = (eventStartDate: Date): TimeToEvent => {
  const currentTimestamp = new Date().getTime(); //milliseconds since 1/1/1970
  const eventStartTimestamp = eventStartDate.getTime();

  if (currentTimestamp >= eventStartTimestamp) {
    return NO_TIME_LEFT_TO_EVENT;
  }

  const secondsToEvent = (eventStartTimestamp - currentTimestamp) / 1000;

  const daysLeft = Math.floor(secondsToEvent / SECONDS_IN_A_DAY);
  const hoursLeft = Math.floor((secondsToEvent / SECONDS_IN_AN_HOUR) % HOURS_IN_A_DAY);
  const minutesLeft = Math.floor(
    (secondsToEvent / SECONDS_IN_A_MINUTE) % MINUTES_IN_AN_HOUR
  );
  const secondsLeft = Math.floor(secondsToEvent % SECONDS_IN_A_MINUTE);

  return {
    days: daysLeft,
    hours: hoursLeft,
    minutes: minutesLeft,
    seconds: secondsLeft,
  };
};
